/** Relative Member 360 SPA paths for deep links from chat and other surfaces. */
export const m360Paths = {
  personOverview: (pgaId: string) => `/people/${pgaId}/person/overview`,
  personContact: (pgaId: string) => `/people/${pgaId}/person/contact`,
  personNotes: (pgaId: string) => `/people/${pgaId}/person/notes`,
  personInteractions: (pgaId: string) => `/people/${pgaId}/interactions/interactions`,
  personInteraction: (pgaId: string, interactionId: string) =>
    `/people/${pgaId}/interactions/interactions/${interactionId}`,
  personMembership: (pgaId: string) => `/people/${pgaId}/membership/overview`,
  personCareer: (pgaId: string) => `/people/${pgaId}/career/overview`,
  personEducation: (pgaId: string) => `/people/${pgaId}/education`,
  facility: (facilityId: string) => `/facilities/${facilityId}`,
  facilityContact: (facilityId: string) => `/facilities/${facilityId}/details/contact`,
  section: (sectionId: string) => `/sections/${sectionId}`,
  jobPost: (jobPostId: string) => `/jobs/job-post/${jobPostId}`,
  commerceOrder: (orderId: string) => `/billing/orders/${orderId}`,
  eventOverview: (eventSlug: string) => `/events/${eventSlug}/overview`
} as const;

const M360_HOST_PATTERNS = [
  /^https?:\/\/(?:m360(?:\.portal)?\.(?:pgahq\.com|pga\.org)|(?:[\w-]+\.)?m360\.dev\.pgahq\.com|m360\.portal\.sandboxpga\.org)(?=\/|$)/i,
  /^https?:\/\/localhost(?::\d+)?(?=\/|$)/i,
  /^https?:\/\/127\.0\.0\.1(?::\d+)?(?=\/|$)/i
];

const DANGEROUS_SCHEME = /^(javascript|data|vbscript|file):/i;
const HTTP_SCHEME = /^https?:\/\//i;

export type NormalizedM360Href =
  | { kind: "internal"; path: string }
  | { kind: "external"; href: string }
  | { kind: "blocked" };

const BLOCKED_MEDIA_SCHEME = DANGEROUS_SCHEME;

/** Allow only http(s) image URLs in chat markdown; block javascript/data and other schemes. */
export function normalizeM360ImageSrc(src?: string | null): string | null {
  if (!src) {
    return null;
  }

  const trimmed = src.trim();
  if (!trimmed || BLOCKED_MEDIA_SCHEME.test(trimmed)) {
    return null;
  }

  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    return trimmed;
  }

  if (HTTP_SCHEME.test(trimmed)) {
    return trimmed;
  }

  return null;
}

/** Turn absolute M360 URLs into in-app paths; leave other URLs external. */
export function normalizeM360Href(href: string): NormalizedM360Href {
  const trimmed = href.trim();
  if (!trimmed) {
    return { kind: "external", href: trimmed };
  }

  if (DANGEROUS_SCHEME.test(trimmed)) {
    return { kind: "blocked" };
  }

  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    return { kind: "internal", path: trimmed };
  }

  if (!HTTP_SCHEME.test(trimmed)) {
    return { kind: "blocked" };
  }

  for (const pattern of M360_HOST_PATTERNS) {
    if (pattern.test(trimmed)) {
      try {
        const url = new URL(trimmed);
        return { kind: "internal", path: `${url.pathname}${url.search}${url.hash}` };
      } catch {
        return { kind: "external", href: trimmed };
      }
    }
  }

  return { kind: "external", href: trimmed };
}
